import React from 'react';
import { BathFixtureShell, Circle, Curve, Rect } from './BathFixtureFrame';
import { Bar, Ellipse, TiltedRect } from './ShowerSchematicPrimitives';
import { SHOWERHEAD } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import {
  resolveShowerheadVariant,
  showerheadLayout,
} from '../../3d/geometry/proceduralRenderers/showerLayout';

/**
 * Showerhead elevation — an orthographic projection of the SAME parts the
 * 3D renderer builds (`showerLayout.js` decides both). Variants:
 *   - standard (default): wall flange, arm, 45° drop, tilted head.
 *   - rain: straight arm, vertical stub, flat disc spraying down.
 *   - handheld: slide bar on two brackets, wand leaning out of a holder;
 *     where the inspection found its outlet, the hose hanging to a supply
 *     elbow on the wall (the one part drawn outside the box).
 *   - dual: standard head plus a wand parked in a wall bracket.
 *
 * Front view (width × height) looks along -z, so the arm is foreshortened
 * to the flange and only the drop and the head's elliptical silhouette
 * show. Side views (depth × height) show the arm's reach and drop angle.
 * Wall at the left edge of a left view; the right view mirrors it.
 */

export { resolveShowerheadVariant };

export function ShowerheadSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  const dims = { width: widthMeters, height: heightMeters, depth: depthMeters };
  const L = showerheadLayout(dims, resolveShowerheadVariant(obj), obj);
  const stroke = { strokeColor, strokeWidth };
  if (view === 'left' || view === 'right') {
    const map = ([z, y]) => [z + depthMeters / 2, heightMeters / 2 - y];
    return (
      <BathFixtureShell width={depthMeters} height={heightMeters}>
        <g
          transform={
            view === 'right'
              ? `translate(${depthMeters}, 0) scale(-1, 1)`
              : undefined
          }
        >
          <SideParts L={L} map={map} stroke={stroke} />
        </g>
      </BathFixtureShell>
    );
  }
  const map = ([x, y]) => [x + widthMeters / 2, heightMeters / 2 - y];
  return (
    <BathFixtureShell width={widthMeters} height={heightMeters}>
      <FrontParts L={L} map={map} stroke={stroke} />
    </BathFixtureShell>
  );
}

ShowerheadSchematic.supportsSideViews = true;

// ---------------------------------------------------------------------------
// Front view (x, y)
// ---------------------------------------------------------------------------

/** Silhouette of a disc whose axis is tilted `tilt` off the view direction. */
function TiltedDiscFront({ cx, cy, head, map, stroke }) {
  const s = Math.sin(head.tilt);
  const c = Math.cos(head.tilt);
  const ry = head.r * s;
  const dy = (head.t / 2) * c; // rim and face centres sit this far apart
  const [rx0, ry0] = map([cx, cy + dy]);
  const [, ry1] = map([cx, cy - dy]);
  return (
    <g>
      <Ellipse cx={rx0} cy={ry0} rx={head.r} ry={ry} {...stroke} />
      <Ellipse cx={rx0} cy={ry1} rx={head.r} ry={ry} {...stroke} />
      <Ellipse
        cx={rx0}
        cy={ry1}
        rx={head.r * SHOWERHEAD.plateRadiusFrac}
        ry={ry * SHOWERHEAD.plateRadiusFrac}
        {...stroke}
      />
      <Bar
        from={[cx - head.r, cy + dy]}
        to={[cx - head.r, cy - dy]}
        r={0}
        map={map}
        {...stroke}
      />
      <Bar
        from={[cx + head.r, cy + dy]}
        to={[cx + head.r, cy - dy]}
        r={0}
        map={map}
        {...stroke}
      />
    </g>
  );
}

function FlangeFront({ flange, map, stroke }) {
  const [cx, cy] = map([0, flange.y]);
  return <Circle cx={cx} cy={cy} r={flange.r} {...stroke} />;
}

function StandardFront({ L, map, stroke, x = 0 }) {
  return (
    <g>
      <FlangeFront flange={L.flange} map={map} stroke={stroke} />
      <Bar
        from={[x, L.drop.from.y]}
        to={[x, L.drop.to.y]}
        r={L.armR}
        map={map}
        {...stroke}
      />
      <TiltedDiscFront
        cx={x}
        cy={L.head.cy}
        head={L.head}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}

function RainFront({ L, map, stroke }) {
  const { head, stub } = L;
  return (
    <g>
      <FlangeFront flange={L.flange} map={map} stroke={stroke} />
      <Bar
        from={[0, stub.yTop]}
        to={[0, stub.yBot]}
        r={L.armR}
        map={map}
        {...stroke}
      />
      <TiltedRect
        centre={[0, head.cy]}
        dir={[1, 0]}
        halfAlong={head.r}
        halfAcross={head.t / 2}
        map={map}
        {...stroke}
      />
    </g>
  );
}

function HandheldFront({ L, map, stroke }) {
  const { bar, collar, wand, head } = L;
  const s = Math.sin(wand.tilt);
  const c = Math.cos(wand.tilt);
  return (
    <g>
      <Bar
        from={[0, bar.yBot]}
        to={[0, bar.yTop]}
        r={bar.r}
        map={map}
        {...stroke}
      />
      <Bar
        from={[0, wand.from.y]}
        to={[0, wand.to.y]}
        r={wand.r}
        map={map}
        {...stroke}
      />
      <TiltedRect
        centre={[0, collar.y]}
        dir={[1, 0]}
        halfAlong={collar.r}
        halfAcross={(collar.len / 2) * c + collar.r * s}
        map={map}
        {...stroke}
      />
      <TiltedDiscFront
        cx={0}
        cy={head.cy}
        head={head}
        map={map}
        stroke={stroke}
      />
      <HoseFront L={L} map={map} stroke={stroke} />
    </g>
  );
}

/** A path through the hose's points, each projected by `project`. */
function hosePath(points, project, map) {
  return points
    .map((p, i) => {
      const [x, y] = map(project(p));
      return `${i ? 'L' : 'M'} ${x} ${y}`;
    })
    .join(' ');
}

/** The supply elbow's flange and the hose to it, seen from the front. */
function HoseFront({ L, map, stroke }) {
  const { elbow, hose } = L;
  if (!elbow || !hose) return null;
  const [cx, cy] = map([elbow.x, elbow.y]);
  return (
    <g>
      <Circle cx={cx} cy={cy} r={elbow.flangeR} {...stroke} />
      <Circle cx={cx} cy={cy} r={elbow.r} {...stroke} />
      <Curve d={hosePath(hose.points, (p) => [p.x, p.y], map)} {...stroke} />
    </g>
  );
}

/** The supply elbow standing off the wall and the hose to it, from the side. */
function HoseSide({ L, map, stroke }) {
  const { elbow, hose } = L;
  if (!elbow || !hose) return null;
  return (
    <g>
      <Bar
        from={[elbow.z0, elbow.y]}
        to={[elbow.z1, elbow.y]}
        r={elbow.r}
        map={map}
        {...stroke}
      />
      <Curve d={hosePath(hose.points, (p) => [p.z, p.y], map)} {...stroke} />
    </g>
  );
}

function DualFront({ L, map, stroke }) {
  const { wand } = L;
  return (
    <g>
      <StandardFront L={L.fixed} map={map} stroke={stroke} />
      <Bar
        from={[wand.x, wand.yBot]}
        to={[wand.x, wand.yJoin]}
        r={wand.r}
        map={map}
        {...stroke}
      />
      <TiltedDiscFront
        cx={wand.x}
        cy={wand.head.cy}
        head={wand.head}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}

function FrontParts({ L, map, stroke }) {
  switch (L.kind) {
    case 'rain':
      return <RainFront L={L} map={map} stroke={stroke} />;
    case 'handheld':
      return <HandheldFront L={L} map={map} stroke={stroke} />;
    case 'dual':
      return <DualFront L={L} map={map} stroke={stroke} />;
    default:
      return <StandardFront L={L} map={map} stroke={stroke} />;
  }
}

// ---------------------------------------------------------------------------
// Side view (z, y) — wall at z = wallZ
// ---------------------------------------------------------------------------

/** A head as seen from the side: a rectangle 2r × t with its face along `axis` (z, y). */
function HeadSide({ cz, cy, head, axis, map, stroke }) {
  const plane = [-axis[1], axis[0]];
  const faceOff = head.t / 2 + head.faceT / 2;
  return (
    <g>
      <TiltedRect
        centre={[cz, cy]}
        dir={plane}
        halfAlong={head.r}
        halfAcross={head.t / 2}
        map={map}
        {...stroke}
      />
      <TiltedRect
        centre={[cz + axis[0] * faceOff, cy + axis[1] * faceOff]}
        dir={plane}
        halfAlong={head.r * SHOWERHEAD.plateRadiusFrac}
        halfAcross={head.faceT / 2}
        map={map}
        {...stroke}
      />
    </g>
  );
}

function FlangeArmSide({ L, map, stroke }) {
  const { flange, arm, elbow } = L;
  const [fx, fy] = map([L.wallZ, flange.y + flange.r]);
  const [ex, ey] = map([elbow.z, elbow.y]);
  return (
    <g>
      <Rect x={fx} y={fy} width={flange.t} height={flange.r * 2} {...stroke} />
      <Bar
        from={[arm.z0, arm.y]}
        to={[arm.z1, arm.y]}
        r={L.armR}
        map={map}
        {...stroke}
      />
      <Circle cx={ex} cy={ey} r={elbow.r} {...stroke} />
    </g>
  );
}

/** The side-view (z, y) direction a head's spray face points, from its tilt. */
const faceAxis = (head) => [Math.sin(head.tilt), -Math.cos(head.tilt)];

function StandardSide({ L, map, stroke }) {
  const { drop, joint, head } = L;
  const [jx, jy] = map([joint.z, joint.y]);
  const axis = faceAxis(head);
  return (
    <g>
      <FlangeArmSide L={L} map={map} stroke={stroke} />
      <Bar
        from={[drop.from.z, drop.from.y]}
        to={[drop.to.z, drop.to.y]}
        r={L.armR}
        map={map}
        {...stroke}
      />
      <Circle cx={jx} cy={jy} r={joint.r} {...stroke} />
      <HeadSide
        cz={head.cz}
        cy={head.cy}
        head={head}
        axis={axis}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}

function RainSide({ L, map, stroke }) {
  const { stub, head } = L;
  return (
    <g>
      <FlangeArmSide L={L} map={map} stroke={stroke} />
      <Bar
        from={[stub.z, stub.yTop]}
        to={[stub.z, stub.yBot]}
        r={L.armR}
        map={map}
        {...stroke}
      />
      <HeadSide
        cz={head.cz}
        cy={head.cy}
        head={head}
        axis={[0, -1]}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}

function HandheldSide({ L, map, stroke }) {
  const { bar, brackets, holder, collar, wand, head } = L;
  const axis = [Math.sin(wand.tilt), Math.cos(wand.tilt)];
  return (
    <g>
      <Bar
        from={[bar.z, bar.yBot]}
        to={[bar.z, bar.yTop]}
        r={bar.r}
        map={map}
        {...stroke}
      />
      {brackets.ys.map((y) => (
        <Bar
          key={y}
          from={[brackets.z0, y]}
          to={[brackets.z1, y]}
          r={brackets.r}
          map={map}
          {...stroke}
        />
      ))}
      <Bar
        from={[holder.z0, holder.y]}
        to={[holder.z1, holder.y]}
        r={holder.r}
        map={map}
        {...stroke}
      />
      <Bar
        from={[wand.from.z, wand.from.y]}
        to={[wand.to.z, wand.to.y]}
        r={wand.r}
        map={map}
        {...stroke}
      />
      <TiltedRect
        centre={[collar.z, collar.y]}
        dir={[-axis[1], axis[0]]}
        halfAlong={collar.r}
        halfAcross={collar.len / 2}
        map={map}
        {...stroke}
      />
      <HeadSide
        cz={head.cz}
        cy={head.cy}
        head={head}
        axis={faceAxis(head)}
        map={map}
        stroke={stroke}
      />
      <HoseSide L={L} map={map} stroke={stroke} />
    </g>
  );
}

function DualSide({ L, map, stroke }) {
  const { wand } = L;
  return (
    <g>
      <StandardSide L={L.fixed} map={map} stroke={stroke} />
      <Bar
        from={[wand.holder.z0, wand.holder.y]}
        to={[wand.holder.z1, wand.holder.y]}
        r={wand.holder.r}
        map={map}
        {...stroke}
      />
      <Bar
        from={[wand.z, wand.yBot]}
        to={[wand.z, wand.yJoin]}
        r={wand.r}
        map={map}
        {...stroke}
      />
      <HeadSide
        cz={wand.z}
        cy={wand.head.cy}
        head={wand.head}
        axis={faceAxis(wand.head)}
        map={map}
        stroke={stroke}
      />
    </g>
  );
}

function SideParts({ L, map, stroke }) {
  switch (L.kind) {
    case 'rain':
      return <RainSide L={L} map={map} stroke={stroke} />;
    case 'handheld':
      return <HandheldSide L={L} map={map} stroke={stroke} />;
    case 'dual':
      return <DualSide L={L} map={map} stroke={stroke} />;
    default:
      return <StandardSide L={L} map={map} stroke={stroke} />;
  }
}
